import {
  ComponentRef,
  contentChildren,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  Renderer2,
  untracked,
  ViewContainerRef
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Select } from '@openng/optimus-ui/select';
import { Tab, Tabs } from '@openng/optimus-ui/tabs';
import { Subscription } from 'rxjs';
import { LayoutService } from '../layout/services/layout.service';
import { TABS_CONFIG } from './tabs.config';

@Directive()
export class TabsPhoneSelectDirective {
  private readonly tabs = inject(Tabs);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly transloco = inject(TranslocoService);
  private readonly layoutService = inject(LayoutService);
  private readonly config = inject(TABS_CONFIG, { optional: true });
  private readonly tabItems = contentChildren(Tab, { descendants: true });

  private select?: ComponentRef<Select>;
  private tabList?: HTMLElement;
  private observer?: MutationObserver;
  private labelSubscription?: Subscription;
  private lastOptions: { label: string; value: string | number; disabled: boolean }[] = [];

  constructor() {
    if (!this.config?.phoneSelect) {
      return;
    }

    effect(() => {
      const active = !this.layoutService.isDesktopView() && this.tabItems().length > 1;
      untracked(() => (active ? this.show() : this.hide()));
    });

    effect(() => {
      this.tabItems().forEach(tab => {
        tab.value();
        tab.disabled();
      });
      untracked(() => this.refresh());
    });

    effect(() => {
      const value = this.tabs.value();
      untracked(() => this.select?.instance.writeValue(value));
    });

    inject(DestroyRef).onDestroy(() => {
      this.observer?.disconnect();
      this.labelSubscription?.unsubscribe();
    });
  }

  private show(): void {
    const tabList = this.host.nativeElement.querySelector<HTMLElement>(':scope > p-tablist');
    if (!tabList || this.select) {
      return;
    }
    this.tabList = tabList;
    this.select = this.viewContainerRef.createComponent(Select);
    const { instance } = this.select;
    this.select.setInput('optionLabel', 'label');
    this.select.setInput('optionValue', 'value');
    this.select.setInput('optionDisabled', 'disabled');
    this.select.setInput('fluid', true);
    this.select.setInput('scrollHeight', '24rem');
    this.labelSubscription = this.transloco
      .selectTranslate('select_section')
      .subscribe(label => this.select?.setInput('ariaLabel', label));
    instance.registerOnChange((value: string | number) => this.tabs.value.set(value));
    instance.writeValue(this.tabs.value());
    tabList.parentNode?.insertBefore(this.select.location.nativeElement, tabList);
    this.renderer.setStyle(tabList, 'display', 'none');

    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(tabList, { characterData: true, subtree: true });
    this.refresh();
  }

  private hide(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.labelSubscription?.unsubscribe();
    this.select?.destroy();
    this.select = undefined;
    this.lastOptions = [];
    if (this.tabList) {
      this.renderer.removeStyle(this.tabList, 'display');
      this.tabList = undefined;
    }
  }

  private refresh(): void {
    if (!this.select) {
      return;
    }
    const options = this.tabItems().map(tab => ({
      label: (tab.el.nativeElement.textContent ?? '').replace(/\s+/g, ' ').trim(),
      value: tab.value() as string | number,
      disabled: tab.disabled()
    }));
    const unchanged =
      options.length === this.lastOptions.length &&
      options.every(
        (o, i) =>
          o.label === this.lastOptions[i].label &&
          o.value === this.lastOptions[i].value &&
          o.disabled === this.lastOptions[i].disabled
      );
    if (!unchanged) {
      this.lastOptions = options;
      this.select.setInput('options', options);
    }
  }
}
